import React, { useState, useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip, Tag } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateControlPanelDrawerPlacement,
  updateLiftSelected,
  updateBankSelected,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
// INTERFACES
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { RootState } from "../../../app/store";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface } from "../../../interfaces/Bank.interface";
// COMPONENTS
import { LiftWarningIcon } from "../iconDefinition/LiftDegradedOverlay";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import { defaultLiftPanelTab } from "../../../types/monitoringAccess";
import { useCanOpenControlPanel } from "../../../customHooks/useScopedPermission";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// UTILS
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import { stringToUuidT } from "../../../utils/uuid";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";

function MonitoringElevatorFullViewName({
  lift,
  bank,
  serverId,
  channelId,
  enableActions,
}: {
  lift: LiftInterface;
  bank: BankInterface;
  serverId: string;
  channelId: string;
  enableActions: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [liftId, setLiftId] = useState<string>("");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const previewFaultServiceMode = useSelector(
    (state: RootState) => state.monitoring.previewFaultServiceMode,
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(liftId), [liftId]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = previewFaultServiceMode || socketMessageLift.faults;

  const serviceModeInfo = useMemo(
    () =>
      getServiceModeDisplay(
        socketMessageLift.serviceMode?.messageType ?? null,
        socketMessageLift.serviceMode?.message?.message?.message ?? null,
      ),
    [socketMessageLift.serviceMode],
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getTabKey = () => {
    // Actions is the default tab for the drawer whenever it's available.
    if (hasPermission(Permission.MonitorControlPanelActions, permissionSet ?? [])) {
      return "ACTIONS";
    }
    if (hasFaults) {
      return "FAULTS";
    }
    return defaultLiftPanelTab(permissionSet ?? []);
  };

  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  // The lift name opens the panel only when at least one of the panel's tabs
  // would render for this role (see CONTROL_PANEL_TAB_GATES); MASTER ADMIN always.
  // Deliberately NOT tied to the Banks switch: the Roles editor's Monitoring
  // Dashboard Banks block shows the lifts, it does not make their names clickable.
  const canOpenLiftControlPanel = useCanOpenControlPanel("lift");

  const onClickOpenModalElevator = (e: any) => {
    // With no tab to show, the lift name is inert; the per-tab permissions only
    // matter once the panel IS open.
    if (!canOpenLiftControlPanel) return;
    if (enableActions) {
      getDrawerPlacement(e);
      dispatch(
        updateModalControlPanelTab({
          liftType: LiftType[LiftType.ELEVATOR],
          tab: getTabKey(),
        }),
      );
      dispatch(updateLiftSelected(lift));
      dispatch(updateBankSelected(bank));
      dispatch(updateChannelId(channelId));
      dispatch(updateServerId(serverId));
      dispatch(updateOpenModalControlPanelLift(true));
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (lift && lift.id && String(lift.id) !== "") {
      setLiftId(String(lift.id));
    }
  }, [lift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div
      className="mt-2"
      role="button"
      tabIndex={0}
      onKeyDown={emptyFunctionDeclaration}
      onClick={onClickOpenModalElevator}
      style={{ fontSize: "9px", fontWeight: "bolder" }}
    >
      <div>
        <Tooltip placement="top" title={lift.name}>
          <span className={hasFaults ? "generalStyles__error" : ""}>
            {String(lift.name ?? "")
              .toUpperCase()
              .substring(0, 6)}
          </span>
          <LiftWarningIcon lift={lift} />
        </Tooltip>
      </div>
      {!hasFaults && !serviceModeInfo && <div style={{ height: "10px" }} />}
      {hasFaults && (
        <div>
          <Tag
            style={{
              fontSize: "9px",
              width: "100%",
              marginTop: "5px",
            }}
            color="red"
          >
            FAULT
          </Tag>
        </div>
      )}
      {(serviceModeInfo || previewFaultServiceMode) &&
        (() => {
          const display = serviceModeInfo;
          if (!display) return null;
          return (
            <div>
              <Tooltip placement="top" title={display.displayName}>
                <Tag
                  style={{
                    width: "100%",
                    fontSize: "10px",
                    marginTop: "5px",
                  }}
                  color={display.color}
                >
                  {display.abbreviation.substring(0, 4)}
                </Tag>
              </Tooltip>
            </div>
          );
        })()}
      {!serviceModeInfo && previewFaultServiceMode && (
        <div>
          <Tooltip placement="top" title="Energy Saving">
            <Tag
              style={{
                width: "100%",
                fontSize: "10px",
                marginTop: "5px",
              }}
              color="blue"
            >
              ENER
            </Tag>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

export default React.memo(MonitoringElevatorFullViewName);
